import { useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { OtpInput } from '@vox/frontend-react-ui/input-otp';
import { InputText } from 'primereact/inputtext';
import { LOGIN_ROLE_OPTIONS } from '@/entities/Auth/config/login-role.config';
import { createRHFFields } from '@/shared/ui/RHFFields';
import type { CredentialsFormValues } from '@/entities/Auth/model/schema';
import { useAuthFlow } from './useAuthFlow';
import classes from './AuthPage.module.scss';

const {
    PhoneField: AuthPhoneField,
    SelectField: AuthRoleField,
} = createRHFFields<CredentialsFormValues>();

/** Отображает двухэтапную форму входа сотрудника */
const AuthPage = () => {
    const [showPassword, setShowPassword] = useState(false);

    const {
        credentialsForm,
        verificationForm,
        isVerificationStep,
        submitCredentials,
        submitVerification,
    } = useAuthFlow();

    return (
        <main className={classes.authPage}>
            <h1 className={classes.title}>Авторизация</h1>

            {!isVerificationStep
                ? (
                    <FormProvider {...credentialsForm}>
                        <form
                            className={classes.form}
                            onSubmit={credentialsForm.handleSubmit(submitCredentials)}
                            noValidate
                        >
                            <AuthPhoneField
                                name="phone"
                                label="Телефон"
                                isRequired
                            />

                            <Controller
                                name="password"
                                control={credentialsForm.control}
                                render={({ field, fieldState }) => (
                                    <div className={classes.field}>
                                        <label
                                            htmlFor="password"
                                            className={classes.requiredLabel}
                                        >
                                            Пароль
                                        </label>
                                        <div className={classes.passwordRow}>
                                            <InputText
                                                id="password"
                                                name={field.name}
                                                ref={field.ref}
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                type={showPassword
                                                    ? 'text'
                                                    : 'password'
                                                }
                                                autoComplete="current-password"
                                                aria-required="true"
                                                aria-invalid={fieldState.invalid}
                                                aria-describedby={fieldState.error
                                                    ? 'password-error'
                                                    : undefined
                                                }
                                                className={classes.input}
                                            />
                                            <button
                                                type="button"
                                                className={classes.passwordToggle}
                                                onClick={() => setShowPassword((visible) => !visible)}
                                                aria-label={showPassword
                                                    ? 'Скрыть пароль'
                                                    : 'Показать пароль'
                                                }
                                            >
                                                {showPassword
                                                    ? 'Скрыть'
                                                    : 'Показать'}
                                            </button>
                                        </div>
                                        {fieldState.error && (
                                            <small
                                                id="password-error"
                                                role="alert"
                                                className={classes.error}
                                            >
                                                {fieldState.error.message}
                                            </small>
                                        )}
                                    </div>
                                )}
                            />

                            <AuthRoleField
                                name="role"
                                label="Роль"
                                options={LOGIN_ROLE_OPTIONS}
                                isRequired
                            />

                            <button
                                type="submit"
                                className={classes.submitButton}
                                disabled={credentialsForm.formState.isSubmitting}
                            >
                                Войти
                            </button>
                        </form>
                    </FormProvider>
                )
                : (
                    <FormProvider {...verificationForm}>
                        <form
                            className={classes.form}
                            onSubmit={verificationForm.handleSubmit(submitVerification)}
                            noValidate
                        >
                            <Controller
                                name="code"
                                control={verificationForm.control}
                                render={({ field, fieldState }) => (
                                    <div className={classes.field}>
                                        <span
                                            id="verification-code-label"
                                            className={classes.requiredLabel}
                                        >
                                            Код подтверждения
                                        </span>
                                        <OtpInput
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            ref={field.ref}
                                            unstyled={false}
                                            length={4}
                                            integerOnly
                                            autoFocus
                                            invalid={fieldState.invalid}
                                            aria-labelledby="verification-code-label"
                                            aria-describedby={fieldState.error
                                                ? 'verification-code-hint verification-code-error'
                                                : 'verification-code-hint'
                                            }
                                            onBlur={field.onBlur}
                                            classNames={{ root: classes.otp }}
                                        />
                                        <small
                                            id="verification-code-hint"
                                            className={classes.codeHint}
                                        >
                                            Введите четырёхзначный код из Telegram или VK
                                        </small>
                                        {fieldState.error && (
                                            <small
                                                id="verification-code-error"
                                                role="alert"
                                                className={classes.error}
                                            >
                                                {fieldState.error.message}
                                            </small>
                                        )}
                                    </div>
                                )}
                            />
                            <button
                                type="submit"
                                className={classes.submitButton}
                                disabled={verificationForm.formState.isSubmitting}
                            >
                                Подтвердить
                            </button>
                        </form>
                    </FormProvider>
                )}
        </main>
    );
};

export default AuthPage;
